import { useState } from "react";
import { CalendarDays, Hourglass, Loader2 } from "lucide-react";
import { dateNumerology, dateDuration } from "@/lib/api";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export default function DateTools() {
  const [numDate, setNumDate] = useState(today());
  const [numRes, setNumRes] = useState(null);
  const [numLoading, setNumLoading] = useState(false);

  const [start, setStart] = useState("2017-08-21");
  const [end, setEnd] = useState("2024-02-26");
  const [durRes, setDurRes] = useState(null);
  const [durLoading, setDurLoading] = useState(false);

  const runNumerology = async () => {
    setNumLoading(true);
    try {
      setNumRes(await dateNumerology(numDate));
    } catch {
      toast.error("Invalid date");
    } finally {
      setNumLoading(false);
    }
  };

  const runDuration = async () => {
    setDurLoading(true);
    try {
      setDurRes(await dateDuration(start, end));
    } catch {
      toast.error("Invalid dates");
    } finally {
      setDurLoading(false);
    }
  };

  return (
    <section className="space-y-10">
      <div className="rise">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted mb-3">
          // Date Decoding Tools
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
          The <span className="gold italic">temporal</span> layer.
        </h1>
        <p className="muted mt-4 max-w-2xl leading-relaxed">
          Date numerology (full + reduced) and exact durations between events — the kind that
          show up as &ldquo;6 years 6 months 6 days&rdquo; or &ldquo;33 weeks and 3 days&rdquo;.
        </p>
      </div>

      {/* === DATE NUMEROLOGY === */}
      <div className="rise rise-1 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="panel p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <CalendarDays className="gold" size={18} />
            <h2 className="font-display text-2xl">Date Numerology</h2>
          </div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] muted">Date</label>
          <div className="mt-2 flex gap-3">
            <input
              data-testid="date-numerology-input"
              type="date"
              value={numDate}
              onChange={(e) => setNumDate(e.target.value)}
              className="terminal-input px-4 py-3 flex-1 text-base"
            />
            <button
              data-testid="date-numerology-submit"
              onClick={runNumerology}
              disabled={numLoading}
              className="btn-primary px-5 py-3 text-xs"
            >
              {numLoading ? <Loader2 className="animate-spin" size={14} /> : "Decode"}
            </button>
          </div>

          {numRes && (
            <div className="mt-6 space-y-4 rise" data-testid="numerology-result">
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] muted">
                  {numRes.weekday}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Full" value={numRes.numerology.full} hint={`${numRes.month}+${numRes.day}+${numRes.year}`} />
                <Stat label="Reduced Full" value={numRes.numerology.reduced_full} />
                <Stat label="Short" value={numRes.numerology.short} hint={`${numRes.month}+${numRes.day}+${numRes.year_short}`} />
                <Stat label="Reduced Short" value={numRes.numerology.reduced_short} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10">
                <Stat label="Day of Year" value={numRes.day_of_year} />
                <Stat label="Days Remaining" value={numRes.days_remaining} />
              </div>
            </div>
          )}
        </div>

        {/* === DATE DURATION === */}
        <div className="panel p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Hourglass className="gold" size={18} />
            <h2 className="font-display text-2xl">Date Duration</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] muted">Start</label>
              <input
                data-testid="date-duration-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="terminal-input mt-2 px-4 py-3 w-full text-base"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] muted">End</label>
              <input
                data-testid="date-duration-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="terminal-input mt-2 px-4 py-3 w-full text-base"
              />
            </div>
          </div>
          <button
            data-testid="date-duration-submit"
            onClick={runDuration}
            disabled={durLoading}
            className="btn-primary mt-4 px-5 py-3 text-xs w-full"
          >
            {durLoading ? <Loader2 className="animate-spin inline mr-2" size={14} /> : null}
            Compute Duration
          </button>

          {durRes && (
            <div className="mt-6 space-y-4 rise" data-testid="duration-result">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Years" value={durRes.breakdown.years} />
                <Stat label="Months" value={durRes.breakdown.months} />
                <Stat label="Days" value={durRes.breakdown.days} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10">
                <Stat label="Total Days" value={durRes.totals.days} />
                <Stat label="Total Weeks" value={durRes.totals.weeks} />
                <Stat
                  label="Weeks & Days"
                  value={`${durRes.totals.weeks_and_days.weeks}w ${durRes.totals.weeks_and_days.days}d`}
                />
                <Stat label="≈ Years" value={durRes.totals.years_approx} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="border border-white/10 p-4 bg-black/30">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] muted">{label}</div>
      <div className="font-mono big-number text-3xl mt-1">{value}</div>
      {hint && <div className="font-mono text-[10px] muted mt-1">{hint}</div>}
    </div>
  );
}
